'use client'
import { Card } from '@/components/ui/card'
import { Activity, TrendingUp, Users, Server, Loader2, ChevronDown, X, Wifi, Monitor, Search, RefreshCw } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import apiClient from '@/lib/noc-api-client'
import { useLanguage } from '@/contexts/LanguageContext'

const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
const imgVector = `${basePath}/watermark-vector.svg`

interface OrgMetrics {
  uptime_percentage: number
  total_devices: number
  online_devices: number
  offline_devices: number
  total_clients: number
  network_count?: number
  network_name?: string
}

interface ClientRecord {
  mac: string
  name: string
  ip: string
  ssid: string
  vlan: string
  status: string
  network_id: string
  network_name: string
  connection: string
}

interface NetworkOption {
  id: string
  name: string
}

interface OrganizationMetricsProps {
  networks?: NetworkOption[]
}

const getUptimeColor = (uptime: number) => {
  if (uptime >= 95) return '#1CA700'
  if (uptime >= 90) return '#F5A623'
  return '#CD585A'
}

export function OrganizationMetrics({ networks = [] }: OrganizationMetricsProps) {
  const { t } = useLanguage()
  const om = t.organizationMetrics
  const [selectedNetworkId, setSelectedNetworkId] = useState<string>('all')
  const [metrics, setMetrics] = useState<OrgMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Client drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [clientsLoading, setClientsLoading] = useState(false)
  const [clients, setClients] = useState<ClientRecord[]>([])
  const [clientSearch, setClientSearch] = useState('')

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true)
        setError(null)
        const url =
          selectedNetworkId === 'all'
            ? '/api/dashboard/organization-metrics'
            : `/api/dashboard/network-metrics/${selectedNetworkId}`
        const data = await apiClient(url)
        setMetrics(data)
      } catch (err) {
        console.error('Failed to fetch metrics:', err)
        setError('Failed to load metrics')
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    }
    fetchMetrics()
    // Auto-refresh every 60 seconds (live data)
    const interval = setInterval(fetchMetrics, 60000)
    return () => clearInterval(interval)
  }, [selectedNetworkId])

  const handleManualRefresh = async () => {
    try {
      setRefreshing(true)
      setError(null)
      const url =
        selectedNetworkId === 'all'
          ? '/api/dashboard/organization-metrics'
          : `/api/dashboard/network-metrics/${selectedNetworkId}`
      const data = await apiClient(url)
      setMetrics(data)
    } catch (err) {
      console.error('Failed to fetch metrics:', err)
      setError('Failed to load metrics')
    } finally {
      setRefreshing(false)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const fetchClients = async () => {
    try {
      setClientsLoading(true)
      setClients([])
      const url =
        selectedNetworkId === 'all'
          ? '/api/dashboard/active-clients'
          : `/api/dashboard/active-clients?network_id=${selectedNetworkId}`
      const data = await apiClient(url)
      setClients(data.clients || [])
    } catch (err) {
      console.error('Failed to fetch client list:', err)
    } finally {
      setClientsLoading(false)
    }
  }

  const handleClientCountClick = () => {
    setDrawerOpen(true)
    setClientSearch('')
    fetchClients()
  }

  const selectedLabel =
    selectedNetworkId === 'all'
      ? om.allNetworks
      : networks.find((n) => n.id === selectedNetworkId)?.name ?? om.allNetworks

  const uptimeColor = metrics ? getUptimeColor(metrics.uptime_percentage) : '#6D6D6D'
  const isTrendingUp = metrics ? metrics.uptime_percentage >= 95 : false

  const clientSubtext =
    selectedNetworkId === 'all'
      ? `${om.across} ${metrics?.network_count ?? '—'} ${om.networks}`
      : metrics?.network_name ?? selectedLabel

  const filteredClients = clients.filter((c) => {
    const q = clientSearch.toLowerCase()
    return (
      !q ||
      c.name.toLowerCase().includes(q) ||
      c.ip.toLowerCase().includes(q) ||
      c.ssid.toLowerCase().includes(q) ||
      c.network_name.toLowerCase().includes(q)
    )
  })

  return (
    <>
      <div className="flex flex-col gap-3">
        {/* Network switcher bar */}
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-[#939393] font-medium tracking-wide uppercase">
            {om.sectionTitle}
          </span>

          <div className="flex items-center gap-2">
            {/* Manual refresh button */}
            <button
              onClick={handleManualRefresh}
              disabled={refreshing || loading}
              className="p-1.5 rounded-[6px] hover:bg-[#F5F5F5] transition-colors disabled:opacity-50"
              title="Refresh now"
            >
              <RefreshCw className={`h-3.5 w-3.5 text-[#939393] ${refreshing ? 'animate-spin' : ''}`} />
            </button>

            {/* Custom compact dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F5F5F5] hover:bg-[#EFEFEF] rounded-[8px] transition-colors"
              >
                <span className="text-[12px] font-medium text-[#676767] max-w-[140px] truncate">
                  {selectedLabel}
                </span>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-[#939393] transition-transform duration-150 ${dropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-[#E5E7EB] rounded-[8px] shadow-md min-w-[180px] max-h-[220px] overflow-y-auto py-1">
                  {/* All Networks option */}
                  <button
                    onClick={() => { setSelectedNetworkId('all'); setDropdownOpen(false) }}
                    className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#F5F5F5] transition-colors ${
                      selectedNetworkId === 'all' ? 'text-[#535EA4] font-medium bg-[#F0F3FF]' : 'text-[#4A5565]'
                    }`}
                  >
                    {om.allNetworks}
                  </button>

                  {/* Divider */}
                  {networks.length > 0 && <div className="border-t border-[#F0F0F0] my-1" />}

                  {/* Per-network options */}
                  {networks.map((net) => (
                    <button
                      key={net.id}
                      onClick={() => { setSelectedNetworkId(net.id); setDropdownOpen(false) }}
                      className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#F5F5F5] transition-colors truncate ${
                        selectedNetworkId === net.id ? 'text-[#535EA4] font-medium bg-[#F0F3FF]' : 'text-[#4A5565]'
                      }`}
                      title={net.name}
                    >
                      {net.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 gap-5">
          {/* Card 1: Network Uptime */}
          <Card className="relative overflow-hidden bg-white border border-[#E5E7EB] shadow-none rounded-[10px] px-[25px] pt-[25px] pb-[25px]">
            <div
              className="absolute pointer-events-none"
              style={{ inset: '15.04% -36.21% -258.8% 56.9%' }}
            >
              <img alt="" className="block max-w-none size-full" src={imgVector} />
            </div>
            <div className="relative flex flex-col gap-[12px]">
              <div className="flex gap-[8px] items-center h-[24px]">
                <Server className="h-[20px] w-[20px] text-[#4A5565]" />
                <span className="text-[16px] font-normal text-[#101828] leading-[24px] tracking-[-0.3125px]">
                  {om.networkUptime}
                </span>
              </div>

              <div className="flex flex-col gap-2">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    <span className="text-[14px] text-[#4A5565]">{om.loading}</span>
                  </div>
                ) : error ? (
                  <span className="text-[14px] text-[#4A5565]">{error}</span>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-[24px] font-semibold leading-[32px] tracking-[-0.3125px]"
                        style={{ color: uptimeColor }}
                      >
                        {metrics?.uptime_percentage.toFixed(1)}%
                      </span>
                      {isTrendingUp ? (
                        <TrendingUp className="h-[16px] w-[16px]" style={{ color: uptimeColor }} />
                      ) : (
                        <Activity className="h-[16px] w-[16px]" style={{ color: uptimeColor }} />
                      )}
                    </div>
                    <p className="text-[14px] font-normal text-[#4A5565] leading-[20px] tracking-[-0.1504px]">
                      {`${metrics?.online_devices} ${om.devicesOnline} ${metrics?.total_devices} ${om.devicesOnlineSuffix}`}
                    </p>
                  </>
                )}
              </div>
            </div>
          </Card>

          {/* Card 2: Total Active Clients — clickable to open drawer */}
          <Card className="relative overflow-hidden bg-white border border-[#E5E7EB] shadow-none rounded-[10px] px-[25px] pt-[25px] pb-[25px]">
            <div
              className="absolute pointer-events-none"
              style={{ inset: '15.04% -36.21% -258.8% 56.9%' }}
            >
              <img alt="" className="block max-w-none size-full" src={imgVector} />
            </div>
            <div className="relative flex flex-col gap-[12px]">
              <div className="flex gap-[8px] items-center h-[24px]">
                <Users className="h-[20px] w-[20px] text-[#4A5565]" />
                <span className="text-[16px] font-normal text-[#101828] leading-[24px] tracking-[-0.3125px]">
                  {om.totalActiveClients}
                </span>
                <span className="text-[10px] font-medium text-[#939393] bg-[#F3F4F6] px-1.5 py-0.5 rounded-full">
                  {om.totalActiveClientsScope}
                </span>
              </div>

              <div className="flex flex-col gap-2">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    <span className="text-[14px] text-[#4A5565]">{om.loading}</span>
                  </div>
                ) : error ? (
                  <span className="text-[14px] text-[#4A5565]">{error}</span>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      {/* Clickable count — opens the client list drawer */}
                      <button
                        onClick={handleClientCountClick}
                        className="group flex items-center gap-1.5 focus:outline-none"
                        title="Click to view client list"
                      >
                        <span className="text-[24px] font-semibold leading-[32px] tracking-[-0.3125px] text-[#1CA700] group-hover:underline decoration-dotted underline-offset-4 transition-all">
                          {metrics?.total_clients.toLocaleString()}
                        </span>
                        <TrendingUp className="h-[16px] w-[16px] text-[#1CA700]" />
                        <span className="text-[10px] text-[#939393] group-hover:text-[#535EA4] transition-colors font-medium">
                          View all →
                        </span>
                      </button>
                    </div>
                    <p className="text-[14px] font-normal text-[#4A5565] leading-[20px] tracking-[-0.1504px]">
                      {clientSubtext}
                    </p>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ── Client List Drawer ────────────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[100] flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
            onClick={() => setDrawerOpen(false)}
          />

          {/* Panel */}
          <div className="relative ml-auto h-full w-full max-w-[480px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[#535EA4]" />
                <span className="text-[14px] font-semibold text-[#141414]">Active Clients</span>
                {!clientsLoading && (
                  <span className="text-[11px] font-medium text-white bg-[#535EA4] rounded-full px-2 py-0.5">
                    {filteredClients.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-[10px] text-[#939393]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
                  Real-time · all device types
                </span>
                <button
                  onClick={() => { fetchClients() }}
                  className="p-1 rounded-md hover:bg-[#F5F5F5] text-[#939393] hover:text-[#141414] transition-colors"
                  title="Refresh client list"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="p-1 rounded-md hover:bg-[#F5F5F5] text-[#939393] hover:text-[#141414] transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-5 py-3 border-b border-[#F0F0F0]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#939393]" />
                <input
                  type="text"
                  placeholder="Search by name, IP, SSID, network…"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-[12px] bg-[#F9F9F9] border border-[#E5E7EB] rounded-[8px] focus:outline-none focus:border-[#8AA2DF] transition-colors"
                />
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {clientsLoading ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 className="h-7 w-7 animate-spin text-[#8AA2DF]" />
                  <span className="text-[12px] text-[#939393]">Fetching client list…</span>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-[#939393]">
                  <Users className="h-8 w-8 opacity-30" />
                  <span className="text-[12px]">
                    {clientSearch ? 'No clients match your search' : 'No active clients found'}
                  </span>
                </div>
              ) : (
                <ul className="divide-y divide-[#F3F4F6]">
                  {filteredClients.map((c, idx) => {
                    const isWireless = !!c.ssid || c.connection === 'Wireless'
                    const isOnline = c.status === 'Online'
                    return (
                      <li key={c.mac || idx} className="px-5 py-3 hover:bg-[#FAFAFA] transition-colors">
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div className={`mt-0.5 p-1.5 rounded-md ${isWireless ? 'bg-[#EEF2FF]' : 'bg-[#F0FDF4]'}`}>
                            {isWireless
                              ? <Wifi className="h-3.5 w-3.5 text-[#535EA4]" />
                              : <Monitor className="h-3.5 w-3.5 text-[#1CA700]" />
                            }
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[12px] font-semibold text-[#141414] truncate" title={c.name}>
                                {c.name}
                              </span>
                              {/* Online / Offline badge */}
                              <span className={`shrink-0 text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                                isOnline
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                {isOnline ? 'Online' : 'Offline'}
                              </span>
                            </div>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                              {c.ip && (
                                <span className="text-[10px] text-[#676767] font-mono">{c.ip}</span>
                              )}
                              {c.ssid && (
                                <span className="text-[10px] text-[#535EA4] truncate max-w-[130px]" title={c.ssid}>
                                  {c.ssid}
                                </span>
                              )}
                              {c.vlan && (
                                <span className="text-[10px] text-[#939393]">VLAN {c.vlan}</span>
                              )}
                            </div>

                            <span className="text-[10px] text-[#A4A4A4] mt-0.5 block truncate" title={c.network_name}>
                              {c.network_name}
                            </span>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {/* Footer */}
            {!clientsLoading && filteredClients.length > 0 && (
              <div className="px-5 py-3 border-t border-[#E5E7EB] text-[10px] text-[#939393] text-center">
                Showing {filteredClients.length} of {clients.length} clients · live / 5-min window · all networks
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
